import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import type { DemoAccounts } from './components/auth/DemoAccountsBox';
import * as Sentry from '@sentry/react';
import { useCompany } from './contexts/CompanyContext';
import { useKeyboardShortcuts, useShortcutsList } from './hooks/useKeyboardShortcuts';
import useAppNavigation from './hooks/useAppNavigation';
import { useRouter } from '@tanstack/react-router';
import { homePathFor, safeRedirect, storageAuth } from './routes/auth';
import { paths } from './routes/paths';
import api from './api/axiosConfig';
import AppHeader from './components/AppHeader';
import ErrorBoundary from './components/ErrorBoundary';
import OfflineIndicator from './components/OfflineIndicator';
import AppRouter from './AppRouter';
import { deriveQuoteFlags, type DocMode } from './components/settings/settingsConstants';
import { ordersEnabled, quotesEnabled, primaryNewDocument } from './utils/documentFeatures';
import type { CurrentUser, TenantSettings, TenantBranding, AvailableTenant, LinkedCustomer } from './types';

// Dev-only shell preview — module-level lazy so React doesn't remount on every render
const ShellPreviewPage = import.meta.env.DEV
  ? lazy(() => import('./pages/ShellPreviewPage'))
  : null;
const CustomerShell = lazy(() => import('./components/shell/CustomerShell'));
import './App.css';

const KeyboardShortcutsHelp = lazy(() => import('./components/KeyboardShortcutsHelp'));
const QuickFind = lazy(() => import('./components/quickfind/QuickFind'));
const BottomBar = lazy(() => import('./components/mobile/BottomBar'));
import { openDashboardSection, openStatement } from './components/mobile/dashboardActions';
import { useIsMobile } from './hooks/use-mobile';
import { clearAllOrderDrafts } from './utils/orderDraft';
import { clearDocumentIndex } from './components/quickfind/documentIndex';
const TenantPickerModal = lazy(() => import('./components/TenantPickerModal'));
// Phase 10: Platform maturity components
const CookieConsent = lazy(() => import('./components/CookieConsent'));
const SupportWidget = lazy(() => import('./components/SupportWidget'));
const TrialBanner = lazy(() => import('./components/TrialBanner'));
const MfaSetupBanner = lazy(() => import('./components/auth/MfaSetupBanner'));
const SubscriptionGate = lazy(() => import('./components/SubscriptionGate'));
import LegalAcceptanceGate from './components/LegalAcceptanceGate';
import type { SubscriptionSummary } from './components/SubscriptionGate';

// Import version from package.json
import packageJson from '../package.json';
import IdleSignOut, { idleMinutesFrom } from './components/auth/IdleSignOut';
const APP_VERSION = packageJson.version;

interface TenantSwitchResponse {
  success: boolean;
  data?: {
    subdomain: string;
    companySlug?: string;
    /** Bearer mode: the new access token. */
    token?: string;
    /** Cookie mode: single-use code redeemed by the target subdomain (POST /auth/transfer). */
    transferCode?: string;
    subscription?: SubscriptionSummary;
  };
  error?: string;
}

function getShellPref(): 'shell' | 'classic' {
  try { return localStorage.getItem('customer_shell_version') === 'shell' ? 'shell' : 'classic'; } catch { return 'classic'; }
}

function tryNewLayout(): void {
  try { localStorage.setItem('customer_shell_version', 'shell'); } catch { /* quota */ }
  window.location.reload();
}

/** Read a JSON value from localStorage, or null. */
function readStored<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

/**
 * Restore the signed-in user synchronously (before the first render) so route
 * guards and pages agree on the very first frame — no "Authentication required"
 * flash on a deep link. Ignored while a cross-subdomain auth transfer runs.
 */
function restoreUser(): CurrentUser | null {
  const user = storageAuth.getUser();
  if (!user) {return null;}
  const tenants = readStored<AvailableTenant[]>('bc-portal-available-tenants');
  if (tenants) {user.availableTenants = tenants;}
  return user;
}

function App() {
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(restoreUser);
  const [sessionExpired, setSessionExpired] = useState<boolean>(false);
  const [shellPref] = useState<'shell' | 'classic'>(getShellPref);
  const [showShortcutsHelp, setShowShortcutsHelp] = useState<boolean>(false);
  const [showQuickFind, setShowQuickFind] = useState<boolean>(false);
  const [showTenantPicker, setShowTenantPicker] = useState<boolean>(false);
  const [tenantSettings, setTenantSettings] = useState<TenantSettings | null>(null);
  const [demoMode, setDemoMode] = useState<boolean>(false);
  const [demoAccounts, setDemoAccounts] = useState<DemoAccounts | null>(null);
  const [linkedCustomers, setLinkedCustomers] = useState<LinkedCustomer[] | null>(() => (restoreUser() ? readStored<LinkedCustomer[]>('bc-portal-linked-customers') : null));
  // Subscription enforcement (see workers/src/middleware/subscription.ts) — captured at
  // login/switch-tenant so SubscriptionGate can show state proactively.
  const [subscription, setSubscription] = useState<SubscriptionSummary | null>(() => (restoreUser() ? readStored<SubscriptionSummary>('bc-portal-subscription') : null));
  const { company } = useCompany();

  const router = useRouter();
  const isPhone = useIsMobile();
  const nav = useAppNavigation(!!currentUser);

  // ── Cross-subdomain auth transfer: drop the previous tenant's cached session ──
  // (The user itself is restored synchronously in useState above; where to go is
  // decided by the URL and the route guards in routes/router.tsx.)
  useEffect(() => {
    if (document.cookie.includes('portal_auth_transfer=')) {
      localStorage.removeItem('bc-portal-user');
      localStorage.removeItem('bc-portal-linked-customers');
      localStorage.removeItem('bc-portal-subscription');
      sessionStorage.removeItem('bc_payment_status_disabled');
      sessionStorage.removeItem('bc_credit_memos_disabled');
    }
  }, []);

  // ── Impersonation + storage change listener ──
  useEffect(() => {
    const updateUserFromStorage = () => {
      const savedUser = localStorage.getItem('bc-portal-user');
      if (savedUser) {
        try {
          setCurrentUser(JSON.parse(savedUser) as CurrentUser);
        } catch (_e) {
          // ignore
        }
      }
    };

    const handleImpersonation = () => updateUserFromStorage();
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'token' || e.key === 'bc-portal-user') {
        updateUserFromStorage();
      }
    };

    window.addEventListener('auth:impersonation', handleImpersonation);
    window.addEventListener('storage', handleStorageChange);

    // Listen for auth state changes (fired by login/logout/impersonation flows)
    const handleAuthChange = () => updateUserFromStorage();
    window.addEventListener('auth:statechange', handleAuthChange);

    return () => {
      window.removeEventListener('auth:impersonation', handleImpersonation);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('auth:statechange', handleAuthChange);
    };
  }, []);

  // ── Expired session → force logout ──
  // axiosConfig dispatches `auth:failed` after a 401 whose token refresh also
  // failed (session/JWT expired). Without this, tokens are cleared but the
  // cached `bc-portal-user` keeps the app "logged in", so the dashboard renders
  // empty while every BC call 401s. Here we tear the session down, bounce to
  // login, and flag it so LoginPage explains the user must sign in again.
  useEffect(() => {
    const handleAuthFailed = () => {
      // Only treat as an expiry if a user was actually signed in — a 401 on the
      // login page (e.g. wrong password) also fires this and must NOT be
      // mislabelled "session expired".
      if (!localStorage.getItem('bc-portal-user')) return;

      setCurrentUser(null);
      setLinkedCustomers(null);
      localStorage.removeItem('bc-portal-user');
      localStorage.removeItem('bc-portal-linked-customers');
      localStorage.removeItem('bc-portal-available-tenants');
      sessionStorage.removeItem('bc_payment_status_disabled');
      sessionStorage.removeItem('bc_credit_memos_disabled');

      if (import.meta.env.VITE_SENTRY_DSN) {
        Sentry.setUser(null);
      }

      setSessionExpired(true);
      // Back to sign-in, remembering where they were so they return there afterwards.
      const here = router.state.location;
      const onLogin = here.pathname === '/login' || /^\/[^/]+\/login$/.test(here.pathname);
      void router.navigate({ href: paths.login(onLogin ? null : safeRedirect(here.href)), replace: true });
    };

    window.addEventListener('auth:failed', handleAuthFailed);
    return () => window.removeEventListener('auth:failed', handleAuthFailed);
  }, [router]);

  // URL → page routing, role checks and the "signed-out on a protected page"
  // bounce now live in the route guards (routes/router.tsx). The old popstate
  // handler and history fence are gone: the router owns history, so back/forward
  // move between real entries instead of being re-pushed.

  // ── Tenant branding (public, before login) ──
  useEffect(() => {
    const loadPublicBranding = async () => {
      try {
        const response = await api.get<{ branding?: TenantBranding; demoMode?: boolean; demoAccounts?: DemoAccounts }>('/tenant/branding');
        if (response.data?.branding) {
          setTenantSettings(prev => ({ ...prev, branding: response.data.branding }));
        }
        setDemoMode(response.data?.demoMode ?? false);
        setDemoAccounts(response.data?.demoAccounts ?? null);
      } catch (_error) {
        // Not critical
      }
    };
    loadPublicBranding();
  }, []);

  // ── Full tenant settings (after login) ──
  const refreshTenantSettings = useCallback(async () => {
    if (!currentUser) return;
    try {
      const response = await api.get<{ tenant?: TenantSettings & { settings?: Record<string, unknown> } }>('/tenant/settings');
      if (response.data?.tenant) {
        const settings = response.data.tenant;

        // Features live under tenant.settings.features (not tenant.features), so
        // merge first — the nested `settings` object wins — then normalise.
        const merged = {
          ...settings,
          ...settings?.settings,
        } as TenantSettings;

        // enableQuotes is the authoritative control; salesQuotes/quoteWorkflow are
        // legacy derived flags that can go stale in the DB (e.g. a tenant turned
        // Enable Quotes off but the old salesQuotes=true was never recomputed).
        // Always re-derive them here so the dashboard/sidebar honour enableQuotes.
        if (merged.features?.enableQuotes !== undefined) {
          const { salesQuotes, quoteWorkflow } = deriveQuoteFlags(
            (merged.features?.docMode as DocMode) || 'order',
            merged.features.enableQuotes
          );
          merged.features.salesQuotes = salesQuotes;
          merged.features.quoteWorkflow = quoteWorkflow;
        }

        setTenantSettings(merged);
      }
    } catch (error) {
      console.error('[App] Error loading tenant settings:', error);
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser) {
      refreshTenantSettings();
    }
  }, [currentUser, refreshTenantSettings]);

  // ── Apply tenant branding colors to theme ──
  useEffect(() => {
    const branding = tenantSettings?.branding;
    if (!branding) return;
    const root = document.documentElement;
    const { primaryColor, secondaryColor } = branding;

    if (primaryColor && /^#[0-9a-fA-F]{6}$/.test(primaryColor)) {
      root.style.setProperty('--color-primary', primaryColor);
      root.style.setProperty('--company-primary-color', primaryColor);
      // Derive darker/lighter/hover variants
      const hex = primaryColor.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      const toHex = (v: number) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0');
      root.style.setProperty('--color-primary-dark', `#${toHex(r * 0.75)}${toHex(g * 0.75)}${toHex(b * 0.75)}`);
      root.style.setProperty('--color-primary-light', `#${toHex(r + (255 - r) * 0.3)}${toHex(g + (255 - g) * 0.3)}${toHex(b + (255 - b) * 0.3)}`);
      root.style.setProperty('--color-primary-hover', `#${toHex(r * 0.85)}${toHex(g * 0.85)}${toHex(b * 0.85)}`);
    }
    if (secondaryColor && /^#[0-9a-fA-F]{6}$/.test(secondaryColor)) {
      root.style.setProperty('--color-secondary', secondaryColor);
      root.style.setProperty('--company-secondary-color', secondaryColor);
    }
  }, [tenantSettings?.branding]);

  // ── Login / Logout ──
  const handleLogin = (email: string, customerId: string, portalAccess = 'Auto', customerName?: string, allowedShipToCodes?: string[] | null, role = 'user', customers?: LinkedCustomer[] | null, contactName?: string | null) => {
    setSessionExpired(false);
    const savedUser = localStorage.getItem('bc-portal-user');
    let user: CurrentUser;

    if (savedUser && (role === 'global_admin' || role === 'tenant_admin')) {
      user = JSON.parse(savedUser) as CurrentUser;
    } else {
      user = {
        email,
        customerId,
        portalAccess: portalAccess || 'Auto',
        customerName: customerName || undefined,
        contactName: contactName || undefined,
        allowedShipToCodes: allowedShipToCodes || undefined,
        role: role || 'user'
      };
      localStorage.setItem('bc-portal-user', JSON.stringify(user));
    }

    // Attach availableTenants from localStorage (stored by LoginPage)
    try {
      const savedTenants = localStorage.getItem('bc-portal-available-tenants');
      if (savedTenants) {
        user.availableTenants = JSON.parse(savedTenants) as AvailableTenant[];
      }
    } catch (_e) { /* ignore */ }

    // Attach last login time (stored by LoginPage)
    try {
      const lastLogin = localStorage.getItem('bc-portal-last-login');
      if (lastLogin) user.lastLoginAt = lastLogin;
    } catch (_e) { /* ignore */ }

    // Pick up subscription state stored by LoginPage at login
    try {
      const savedSubscription = localStorage.getItem('bc-portal-subscription');
      setSubscription(savedSubscription ? (JSON.parse(savedSubscription) as SubscriptionSummary) : null);
    } catch (_e) { /* ignore */ }

    setCurrentUser(user);

    // Set Sentry user context for error tracking
    if (import.meta.env.VITE_SENTRY_DSN) {
      Sentry.setUser({ email: user.email, id: user.id });
      Sentry.setTag('userRole', user.role);
    }

    // Store linked customers for multi-customer switcher
    if (customers && customers.length > 1) {
      setLinkedCustomers(customers);
      try {
        localStorage.setItem('bc-portal-linked-customers', JSON.stringify(customers));
      } catch (_e) { /* quota exceeded */ }
    } else {
      setLinkedCustomers(null);
      localStorage.removeItem('bc-portal-linked-customers');
    }

    // Return to the page that sent them to sign-in (same-origin paths only),
    // else the role's home. Replace so Back doesn't land on the sign-in form.
    const search = router.state.location.search as { redirect?: unknown };
    const target = safeRedirect(search.redirect) ?? homePathFor(user);
    void router.navigate({ href: target, replace: true });
  };

  const handleLogout = () => {
    // End the session server-side too (revokes this device's refresh token and,
    // in cookie mode, clears the HttpOnly session cookies). Previously logout only
    // cleared local UI state and left a valid session behind.
    import('./services/DataServiceFactory')
      .then(({ dataService }) => dataService.logout())
      .catch(() => { /* already signed out / offline: local state is cleared below */ });
    setCurrentUser(null);
    setLinkedCustomers(null);
    setSubscription(null);
    localStorage.removeItem('bc-portal-user');
    localStorage.removeItem('bc-portal-linked-customers');
    localStorage.removeItem('bc-portal-available-tenants');
    localStorage.removeItem('bc-portal-subscription');
    localStorage.removeItem('bc-portal-mfa-setup-due');
    clearDocumentIndex();
    // X5: unsent order drafts belong to the person signing out (shared terminals).
    clearAllOrderDrafts();
    sessionStorage.removeItem('bc_payment_status_disabled');
    sessionStorage.removeItem('bc_credit_memos_disabled');

    // Clear Sentry user context
    if (import.meta.env.VITE_SENTRY_DSN) {
      Sentry.setUser(null);
    }

    nav.setCurrentPage('login');
  };

  // ── Multi-customer switch ──
  const handleCustomerSwitch = async (customer: LinkedCustomer) => {
    const { dataService } = await import('./services/DataServiceFactory');
    const customerNo = customer.customerNo || customer.customerId || '';
    const result = await dataService.switchCustomer(
      customerNo,
      customer.customerId || customerNo,
      customer.customerName || ''
    );
    if (!result.success) {
      console.error('[App] Customer switch failed:', result.error);
      alert(`Failed to switch customer: ${result.error || 'Unknown error'}`);
      return;
    }

    // Update current user state
    const updatedUser: CurrentUser = {
      ...currentUser!,
      customerId: customerNo,
      customerName: customer.customerName || customerNo || undefined
    };
    setCurrentUser(updatedUser);
    try {
      localStorage.setItem('bc-portal-user', JSON.stringify(updatedUser));
    } catch (_e) { /* quota exceeded */ }

    // Clear caches and refresh dashboard
    clearDocumentIndex();
    sessionStorage.removeItem('bc_payment_status_disabled');
    sessionStorage.removeItem('bc_credit_memos_disabled');
    nav.refreshDashboard();

    // Navigate to dashboard if not already there
    if (nav.currentPage !== 'dashboard') {
      nav.setCurrentPage('dashboard');
    }
  };

  // ── Tenant switch ──
  const handleTenantSwitch = () => {
    setShowTenantPicker(true);
  };

  const handleTenantSelected = async (tenant: AvailableTenant) => {
    const { dataService } = await import('./services/DataServiceFactory');
    const result = await dataService.switchTenant(tenant.id) as TenantSwitchResponse;
    if (!result.success) {
      console.error('[App] Tenant switch failed:', result.error);
      return;
    }

    const targetSubdomain = result.data!.subdomain;
    const targetSlug = encodeURIComponent(result.data!.companySlug || company?.slug || 'Portal');
    const currentHost = window.location.hostname;

    // Store subscription state from the switch-tenant response. Note: like bc-portal-user,
    // this tenant switch always re-authenticates via the portal_auth_transfer cookie +
    // /auth/me on reload/redirect rather than restoring from localStorage, so this value
    // won't be picked up as SubscriptionGate's initialSummary until the next full login —
    // any 402 in the meantime still surfaces it via the subscription:denied event.
    if (result.data!.subscription) {
      try { localStorage.setItem('bc-portal-subscription', JSON.stringify(result.data!.subscription)); } catch (_e) { /* quota */ }
    }

    // Clear stale data
    localStorage.removeItem('bc-portal-user');
    localStorage.removeItem('bc-portal-linked-customers');
    localStorage.removeItem('bc-portal-available-tenants');
    sessionStorage.removeItem('bc_payment_status_disabled');
    sessionStorage.removeItem('bc_credit_memos_disabled');

    // Hand the session to the target subdomain via a short-lived cookie (60s TTL).
    // Cookie mode: an opaque single-use transfer code (the JWT never reaches JS).
    // Bearer mode (legacy/previews): the access token, as before.
    const handoff = result.data!.transferCode || result.data!.token;
    const sameHost = !targetSubdomain || currentHost.startsWith(targetSubdomain + '.');
    if (sameHost && result.data!.transferCode) {
      // Same subdomain in cookie mode: redeem here, then reload with the new cookies
      await dataService.redeemTransfer(result.data!.transferCode);
      window.location.reload();
      return;
    }
    document.cookie = `portal_auth_transfer=${handoff}; domain=.bcportal.app; path=/; max-age=60; secure; samesite=lax`;

    // Redirect to target subdomain with correct company slug
    if (!sameHost) {
      window.location.href = `https://${targetSubdomain}.bcportal.app/${targetSlug}/login?auth_transfer=1`;
    } else {
      // Same subdomain — just reload with fresh session
      window.location.reload();
    }
  };

  // ── Access control helpers ──
  const isReadOnly = currentUser?.portalAccess === 'ReadOnly';
  const canPlaceOrders = !isReadOnly && currentUser?.portalAccess !== 'Blocked';
  // SETTINGS-WIRING fix 9: one read-time rule for orders/quotes and the primary New button.
  const ordersOn = ordersEnabled(tenantSettings?.features);
  const quotesOn = quotesEnabled(tenantSettings?.features);
  const primaryNew = primaryNewDocument(tenantSettings?.features);

  // ── Keyboard shortcuts ──
  const shortcuts = {
    'show-help': {
      key: '?',
      description: 'Show keyboard shortcuts',
      category: 'general',
      action: () => setShowShortcutsHelp(true)
    },
    'quick-find': {
      key: 'k',
      ctrl: true,
      meta: true,
      description: 'Quick find (pages, order and invoice numbers)',
      category: 'navigation',
      action: () => currentUser && setShowQuickFind(true)
    },
    'go-dashboard': {
      key: 'h',
      ctrl: true,
      description: 'Go to Dashboard',
      category: 'navigation',
      action: () => currentUser && nav.setCurrentPage('dashboard')
    },
    'go-settings': {
      key: ',',
      ctrl: true,
      description: 'Open Settings',
      category: 'navigation',
      action: () => currentUser && nav.setCurrentPage('settings')
    },
    'new-order': {
      key: 'n',
      ctrl: true,
      description: 'Create new order',
      category: 'actions',
      action: () => currentUser && nav.handleNewOrder()
    },
    'back': {
      key: 'Escape',
      description: 'Go back / Close',
      category: 'navigation',
      action: () => {
        if (showShortcutsHelp) {
          setShowShortcutsHelp(false);
        } else if (nav.currentPage === 'support-tickets') {
          nav.handleNavigateHome(currentUser?.role);
        } else if (nav.currentPage === 'order-form' || nav.currentPage === 'settings' || nav.currentPage === 'my-account' || nav.currentPage === 'credit-memo-detail' || nav.currentPage === 'shipment-detail') {
          nav.handleBackToDashboard();
        }
      }
    },
    'refresh': {
      key: 'r',
      ctrl: true,
      description: 'Refresh current view',
      category: 'general',
      action: (e: KeyboardEvent) => {
        e.preventDefault();
        window.location.reload();
      }
    }
  };

  useKeyboardShortcuts(shortcuts, !!currentUser || showShortcutsHelp);
  const shortcutsList = useShortcutsList(shortcuts);

  // ── Render ──

  // Dev-only shell preview — completely bypasses auth/layout so nothing can redirect it
  if (import.meta.env.DEV && ShellPreviewPage && nav.currentPage === 'shell-preview') {
    return (
      <ErrorBoundary>
        <Suspense fallback={<div style={{ padding: 40 }}>Loading shell preview…</div>}>
          <ShellPreviewPage />
        </Suspense>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
    <div className="app" data-version={APP_VERSION}>
      {/* X3: connection status pill (offline / back online) */}
      <OfflineIndicator />

      {/* A17: opt-in idle sign-out for shared terminals (Settings → Security). */}
      {currentUser && <IdleSignOut minutes={idleMinutesFrom(tenantSettings?.security?.idleSignOutMinutes)} onSignOut={handleLogout} />}

      {/* ── Shell toggle ── customer users with 'shell' pref get sidebar layout; everyone else gets classic header */}
      {(() => {
        const isShellMode = currentUser?.role === 'user' && shellPref === 'shell';

        const appRouter = (
          <AppRouter
            currentUser={currentUser}
            currentPage={nav.currentPage}
            setCurrentPage={nav.setCurrentPage}
            tenantSettings={tenantSettings}
            refreshTenantSettings={refreshTenantSettings}
            linkedCustomers={linkedCustomers}
            onCustomerSwitch={handleCustomerSwitch}
            editingOrderNo={nav.editingOrderNo}
            editingQuoteNo={nav.editingQuoteNo}
            viewingInvoiceNo={nav.viewingInvoiceNo}
            viewingCreditMemoNo={nav.viewingCreditMemoNo}
            viewingShipmentNo={nav.viewingShipmentNo}
            selectedShipToCode={nav.selectedShipToCode}
            formMode={nav.formMode}
            prefillOrder={nav.prefillOrder}
            dashboardRefreshKey={nav.dashboardRefreshKey}
            adminTab={nav.adminTab}
            onAdminTabChange={nav.handleAdminTabChange}
            adminRecordId={nav.adminRecordId}
            onOpenAdminRecord={nav.openAdminRecord}
            helpArticle={nav.helpArticle}
            legalSlug={nav.legalSlug}
            onNavigate={nav.goTo}
            handleNewOrder={nav.handleNewOrder}
            handleNewQuote={nav.handleNewQuote}
            handleEditOrder={nav.handleEditOrder}
            handleEditQuote={nav.handleEditQuote}
            handleQuoteConverted={nav.handleQuoteConverted}
            handleViewPostedInvoice={nav.handleViewPostedInvoice}
            handleViewCreditMemo={nav.handleViewCreditMemo}
            handleViewShipment={nav.handleViewShipment}
            handleBackToDashboard={nav.handleBackToDashboard}
            handleNavigateHome={() => nav.handleNavigateHome(currentUser?.role)}
            onMyAccount={nav.handleViewMyAccount}
            handleLogin={handleLogin}
            canPlaceOrders={canPlaceOrders}
            isReadOnly={isReadOnly}
            demoMode={demoMode}
            demoAccounts={demoAccounts}
            sessionExpired={sessionExpired}
          />
        );

        if (isShellMode) {
          return (
            <Suspense fallback={null}>
              <CustomerShell
                currentUser={currentUser}
                currentPage={nav.currentPage}
                tenantSettings={tenantSettings}
                linkedCustomers={tenantSettings?.features?.multiCustomerAccess ? linkedCustomers : null}
                onCustomerSwitch={handleCustomerSwitch}
                onTenantSwitch={handleTenantSwitch}
                onNavigate={nav.setCurrentPage}
                onNewOrder={() => (primaryNew === 'quote' ? nav.handleNewQuote() : nav.handleNewOrder())}
                onLogout={handleLogout}
                onHome={() => nav.handleNavigateHome(currentUser?.role)}
                canPlaceOrders={canPlaceOrders && primaryNew !== null}
                isQuoteWorkflow={primaryNew === 'quote'}
              >
                <LegalAcceptanceGate enabled={!!currentUser} userId={currentUser?.id || currentUser?.email}>
                  {appRouter}
                </LegalAcceptanceGate>
              </CustomerShell>
            </Suspense>
          );
        }

        return (
          <>
            <AppHeader
              currentUser={currentUser ?? undefined}
              appVersion={APP_VERSION}
              currentPage={nav.currentPage}
              userRole={currentUser?.role}
              tenantSettings={tenantSettings ?? undefined}
              linkedCustomers={tenantSettings?.features?.multiCustomerAccess ? linkedCustomers : null}
              onCustomerSwitch={handleCustomerSwitch}
              onTenantSwitch={handleTenantSwitch}
              onMyAccount={nav.handleViewMyAccount}
              onHome={() => nav.handleNavigateHome(currentUser?.role)}
              onLogout={handleLogout}
              onSecurity={() => nav.setCurrentPage('settings')}
              onHelp={() => nav.setCurrentPage('help')}
              onQuickFind={currentUser ? () => setShowQuickFind(true) : undefined}
              onTryNewLayout={currentUser?.role === 'user' ? tryNewLayout : undefined}
            />

            {/* Phase 10: Trial banner — shown to admin users on trial subscriptions */}
            {currentUser && (
              <Suspense fallback={null}>
                <TrialBanner
                  currentUser={{
                    id: currentUser.id || '',
                    role: currentUser.role,
                    tenantId: String(currentUser.tenantId || ''),
                  }}
                  onNavigateToBilling={() => nav.goTo(paths.admin('tntadm', 'billing'))}
                />
              </Suspense>
            )}

            {/* Two-step sign-in grace period reminder (tenant now requires it; user not set up yet) */}
            {currentUser && nav.currentPage !== 'settings' && (
              <Suspense fallback={null}>
                <MfaSetupBanner onSetUp={() => nav.setCurrentPage('settings')} />
              </Suspense>
            )}

            {/* Subscription enforcement — read-only/blocked/trial-ending state (see workers/src/middleware/subscription.ts).
                Suppressed on the admin shells (glbadm/rsladm/tntadm): the blocking modal is
                full-screen (fixed inset-0 z-50) and would otherwise cover the very page an
                admin lands on after clicking "Manage billing" from it, making the subscription
                unfixable. Every other page keeps the gate. */}
            {currentUser && !['glbadm', 'rsladm', 'tntadm'].includes(nav.currentPage) && (
              <Suspense fallback={null}>
                <SubscriptionGate
                  currentUser={{
                    id: currentUser.id || '',
                    role: currentUser.role,
                    tenantId: String(currentUser.tenantId || ''),
                  }}
                  initialSummary={subscription}
                  onNavigateToBilling={() => {
                    if (currentUser.role === 'global_admin') nav.setCurrentPage('glbadm');
                    else if (currentUser.role === 'reseller_admin') nav.setCurrentPage('rsladm');
                    else nav.goTo(paths.admin('tntadm', 'billing'));
                  }}
                />
              </Suspense>
            )}

            <LegalAcceptanceGate enabled={!!currentUser} userId={currentUser?.id || currentUser?.email}>
              {appRouter}
            </LegalAcceptanceGate>
          </>
        );
      })()}

      {/* Phase 10: Support widget — floating button for logged-in users */}
      {currentUser && (
        <Suspense fallback={null}>
          <SupportWidget onNavigateToTickets={() => nav.setCurrentPage('support-tickets')} />
        </Suspense>
      )}

      {/* Tenant Picker Modal (for switching tenants) */}
      {showTenantPicker && (currentUser?.availableTenants?.length ?? 0) > 1 && (
        <Suspense fallback={null}>
          <TenantPickerModal
            tenants={currentUser!.availableTenants ?? null}
            onSelect={handleTenantSelected}
            onCancel={() => setShowTenantPicker(false)}
          />
        </Suspense>
      )}

      {/* Phone bottom bar (N6) — customers on phones; not on the order form or sign-in */}
      {currentUser?.role === 'user' && isPhone && !['order-form', 'login', 'not-found'].includes(nav.currentPage) && (
        <Suspense fallback={null}>
          <BottomBar
            currentPage={nav.currentPage}
            canPlaceOrders={canPlaceOrders}
            quotesEnabled={quotesOn}
            ordersEnabled={ordersOn}
            statementsEnabled={!!tenantSettings?.features?.statements}
            onHome={() => nav.handleNavigateHome(currentUser.role)}
            onInvoices={() => openDashboardSection('invoices', nav.currentPage === 'dashboard', () => nav.setCurrentPage('dashboard'))}
            onSearch={() => setShowQuickFind(true)}
            onAccount={nav.handleViewMyAccount}
            onNewOrder={() => nav.handleNewOrder()}
            onNewQuote={nav.handleNewQuote}
            onReorder={() => openDashboardSection('invoices', nav.currentPage === 'dashboard', () => nav.setCurrentPage('dashboard'))}
            onStatement={() => openStatement(nav.currentPage === 'dashboard', () => nav.setCurrentPage('dashboard'))}
          />
        </Suspense>
      )}

      {/* Quick find — Ctrl-K / ⌘K (N4) */}
      {currentUser && showQuickFind && (
        <Suspense fallback={null}>
          <QuickFind
            open={showQuickFind}
            onOpenChange={setShowQuickFind}
            onSelect={nav.goTo}
            role={currentUser.role}
            customerId={currentUser.customerId}
            slug={nav.companySlug}
            quotesEnabled={quotesOn}
            ordersEnabled={ordersOn}
            canPlaceOrders={canPlaceOrders}
          />
        </Suspense>
      )}

      {/* Keyboard Shortcuts Help - ? */}
      <Suspense fallback={null}>
        <KeyboardShortcutsHelp
          isOpen={showShortcutsHelp}
          onClose={() => setShowShortcutsHelp(false)}
          shortcuts={shortcutsList}
        />
      </Suspense>

      {/* Essential-cookies notice (decision Q4): manages its own visibility */}
      <Suspense fallback={null}>
        <CookieConsent />
      </Suspense>
    </div>
    </ErrorBoundary>
  );
}

export default App;
